import { useState, useEffect, useRef, useCallback } from "react";
import useAutoReset from "../../hooks/useAutoReset";
import { useHotkeys } from "react-hotkeys-hook";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { useHistory, useParams, Prompt } from "react-router-dom";
import { capture } from "../../instrument";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";
import { LuLock, LuTriangleAlert } from "react-icons/lu";
import toast from "react-hot-toast";

import API from "../../api";
import {
  useAdmission,
  getCachedAdmission,
  upsertAdmission,
  writeAdmission,
  removeAdmission,
  getAdmission,
  prefetchAdmission,
  registerLocalOverlay,
  registerActiveAdmission,
} from "../../db/admissions";
import { useSocket } from "../../contexts/SocketContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildServerParams } from "../../db/admissionFilter";
import { LinearProgressBar } from "../../components/Loading";
import EmailPreviewModal from "../../components/EmailPreviewModal";
import { PrescriptionHoverProvider } from "../../components/PrescriptionHoverContext";
import { AdmissionDocumentsViewer, AdmissionHeader, AdmissionOverview, RejectModal, ForceStatusModal, SubmitConfirmModal, CommunicationsSection } from "../../components/admission";
import AuditJournal from "../../components/audit/AuditJournal";
import { useAdmissionPreview } from "../../contexts/AdmissionsContext";
import { ADMISSION_STATUS, FIELD_STATE, EMAIL_TYPE, STATUS_CONFIG, TERMINAL_ADMISSION_STATUSES, isRequiredFieldState, resolveActiveFields } from "../../utils";

const TABS = [
  { id: "overview", label: "Général" },
  { id: "emails", label: "Emails" },
  { id: "history", label: "Historique" },
];

const TabBar = ({ activeTab, onTabChange, counts, indicators }) => (
  <div className="flex h-[38px] gap-4 border-b border-gray-200 bg-white px-4">
    {TABS.map((tab) => {
      const isActive = activeTab === tab.id;
      const count = counts?.[tab.id];
      const indicatorColor = indicators?.[tab.id];
      return (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`relative py-2 text-sm transition-colors ${isActive ? "font-semibold text-primary-600" : "text-gray-500 hover:text-gray-700"}`}
        >
          <span className="inline-flex items-center gap-1.5">
            {tab.label}
            {indicatorColor && <span className={`h-1.5 w-1.5 rounded-full ${indicatorColor}`} title="Sélection active" />}
          </span>
          {count > 0 && <span className={`meta-badge ml-1.5 ${isActive ? "bg-primary-50 text-primary-600" : "bg-gray-100 text-gray-500"}`}>{count}</span>}
          {isActive && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-500" />}
        </button>
      );
    })}
  </div>
);

const getValueAtPath = (obj, path) => path.split(".").reduce((acc, key) => acc?.[key], obj);

const setValueAtPath = (obj, path, value) => {
  const parts = path.split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] === undefined || current[part] === null) current[part] = /^\d+$/.test(parts[i + 1]) ? [] : {};
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
};

const buildDeltaPayload = (draft, fields) => {
  const payload = { _id: draft._id };
  const prescriptionItems = new Map();
  const questionRecordIds = new Set();

  const prescriptionItem = (prescription) => {
    if (!prescriptionItems.has(prescription._id)) prescriptionItems.set(prescription._id, { _id: prescription._id });
    return prescriptionItems.get(prescription._id);
  };

  for (const field of fields) {
    const parts = field.split(".");
    if (parts[0] === "prescriptions") {
      if (parts.length === 1) {
        (draft.prescriptions || []).forEach((prescription) => {
          prescriptionItem(prescription).acts = prescription.acts;
        });
        continue;
      }
      const prescription = draft.prescriptions?.find((p) => p._id === parts[1]);
      if (!prescription) continue;
      const subPath = parts.slice(2).join(".");
      if (subPath) setValueAtPath(prescriptionItem(prescription), subPath, getValueAtPath(prescription, subPath));
    } else if (parts[0] === "questionRecords") {
      questionRecordIds.add(parts[1]);
    } else {
      setValueAtPath(payload, field, getValueAtPath(draft, field));
    }
  }

  if (prescriptionItems.size > 0) payload.prescriptions = [...prescriptionItems.values()];
  if (questionRecordIds.size > 0) {
    payload.questionRecords = (draft.questionRecords || [])
      .filter((questionRecord) => questionRecordIds.has(questionRecord._id))
      .map((questionRecord) => ({ questionId: questionRecord.questionId, answer: questionRecord.answer }));
  }
  return payload;
};

const applyLocalOverlay = (next, current, fields) => {
  for (const field of fields) {
    const parts = field.split(".");
    if (!(parts[0] in next)) continue;
    if (parts[0] === "prescriptions") {
      if (parts.length === 1) {
        next.prescriptions = next.prescriptions.map((prescription) => ({ ...prescription, acts: current.prescriptions.find((p) => p._id === prescription._id)?.acts ?? prescription.acts }));
        continue;
      }
      const currentPrescription = current.prescriptions?.find((p) => p._id === parts[1]);
      const nextPrescription = currentPrescription && (next.prescriptions || []).find((p) => p._id === parts[1]);
      if (!nextPrescription) continue;
      const subPath = parts.slice(2).join(".");
      if (subPath) setValueAtPath(nextPrescription, subPath, getValueAtPath(currentPrescription, subPath));
    } else if (parts[0] === "questionRecords") {
      const currentRecord = current.questionRecords?.find((qr) => qr._id === parts[1]);
      const nextRecord = currentRecord && (next.questionRecords || []).find((qr) => qr._id === parts[1]);
      if (nextRecord) nextRecord.answer = currentRecord.answer;
    } else {
      setValueAtPath(next, field, getValueAtPath(current, field));
    }
  }
};

const AdmissionDetail = ({ user }) => {
  const history = useHistory();
  const { id } = useParams();
  const idRef = useRef(id);
  idRef.current = id;

  const admission = useAdmission(id);

  useEffect(() => registerActiveAdmission(id), [id]);

  const preview = useAdmissionPreview(id);
  const [isFetchingAdmission, setIsFetchingAdmission] = useState(false);

  // Always fetch fresh on navigation: the cached row renders instantly via the
  // live query while the fresh doc replaces it through upsertAdmission.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsFetchingAdmission(true);
    getAdmission(id)
      .catch(() => !cancelled && toast.error("Une erreur est survenue lors de la récupération des détails de l'admission"))
      .finally(() => !cancelled && setIsFetchingAdmission(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLocked = TERMINAL_ADMISSION_STATUSES.includes(admission?.status);
  const { socket } = useSocket();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [forceStatusTarget, setForceStatusTarget] = useState(null);
  const [submitConfirmation, setSubmitConfirmation] = useState(null);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [emailPreviewType, setEmailPreviewType] = useState(EMAIL_TYPE.MORE_QUESTIONS);
  const [originalGuidelineIds, setOriginalGuidelineIds] = useState([]);
  const [defaultActiveFields, setDefaultActiveFields] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const debounceTimerRef = useRef(null);
  const dirtyFieldsRef = useRef(new Set());
  const inFlightFieldsRef = useRef(new Set());
  const invalidFieldsRef = useRef(new Set());
  const detailsScrollRef = useRef(null);
  const [viewerLayoutMode, setViewerLayoutMode] = useState("attach");
  const [autocompleteSettings, setAutocompleteSettings] = useState({ doctor: true });
  const [laboratories, setLaboratories] = useState([]);
  const activeFields = resolveActiveFields(defaultActiveFields, laboratories.find((laboratory) => laboratory._id === admission?.laboratory?._id) || admission?.laboratory);
  const [actionsAppliedToPrescriptions, setActionsAppliedToPrescriptions] = useAutoReset([], 5000);
  const [incompatibleCodesRefused, setIncompatibleCodesRefused] = useAutoReset([], 5000);
  const [presenceCounts, setPresenceCounts] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const queryClient = useQueryClient();
  const [hasBlockedProduction, setHasBlockedProduction] = useState(false);
  const admissionHeaderRef = useRef(null);
  const admissionOverviewRef = useRef(null);
  const admissionDocumentsViewerRef = useRef(null);
  useEffect(() => {
    setActiveTab("overview");
    setHasBlockedProduction(false);
    setValidationErrors([]);
    setForceStatusTarget(null);
    setShowRejectModal(false);
    setSubmitConfirmation(null);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      dirtyFieldsRef.current = new Set();
      inFlightFieldsRef.current = new Set();
      invalidFieldsRef.current = new Set();
      setHasPendingChanges(false);
    };
  }, [id]);

  // Server docs (fetch, save response, socket) pass through this before landing in the
  // collection: locally edited fields keep their local value so an incoming doc can't
  // clobber what the user is typing.
  useEffect(() => {
    return registerLocalOverlay((incoming) => {
      if (incoming._id !== idRef.current) return incoming;
      const current = getCachedAdmission(incoming._id);
      const protectedFields = new Set([...dirtyFieldsRef.current, ...inFlightFieldsRef.current, ...invalidFieldsRef.current]);
      if (!current || protectedFields.size === 0) return incoming;
      const next = { ...incoming };
      applyLocalOverlay(next, current, protectedFields);
      return next;
    });
  }, []);

  const lastProcessingEvent = admission?.lastProcessingEvent;

  const getProcessingItemStatus = (type, referenceId) => {
    if (!lastProcessingEvent?.items) return null;
    const item = lastProcessingEvent.items.find((i) => i.type === type && (referenceId ? i.referenceId?.toString() === referenceId?.toString() : true));
    return item ? item.inserted : null;
  };

  const ProcessingStatusIcon = ({ type, referenceId, className = "h-4 w-4" }) => {
    const inserted = getProcessingItemStatus(type, referenceId);
    if (inserted === true) return <HiCheckCircle className={`${className} flex-shrink-0 text-emerald-500`} title="Inséré dans le SIL" />;
    if (inserted === false) return <HiExclamationCircle className={`${className} flex-shrink-0 text-red-500`} title="Non inséré dans le SIL" />;
    return null;
  };

  useEffect(() => {
    getSettings();
    getLaboratories();
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.emit("joinAdmission", admission?._id);

    return () => {
      if (admission?._id) socket.emit("leaveAdmission", admission?._id);
    };
  }, [admission?._id, socket]);

  useEffect(() => {
    if (!socket) return;

    socket.on("presence-counts", setPresenceCounts);
    return () => socket.off("presence-counts", setPresenceCounts);
  }, [socket]);

  useEffect(() => {
    if (!socket || !id) return;
    const currentId = id;

    const onAdmissionUpdated = (payload) => {
      if (payload?._id !== currentId) return;
      if (payload.deleted) return removeAdmission(currentId);
      if (!payload.doc) return void getAdmission(currentId).catch(() => {});
      upsertAdmission(payload.doc);
    };

    socket.on("admission-updated", onAdmissionUpdated);
    return () => socket.off("admission-updated", onAdmissionUpdated);
  }, [socket, id]);

  useEffect(() => {
    if (!admission?._id) return;
    if (detailsScrollRef.current) detailsScrollRef.current.scrollTo({ top: 0, behavior: "auto" });
  }, [admission?._id]);

  useEffect(() => {
    if (!admission?._id) return;
    setOriginalGuidelineIds((admission.guidelines || []).map((g) => g.guidelineId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admission?._id]);

  // Edits are written straight into the collection's base document, so values never live
  // in droppable overlays: a failed save leaves them in place and only the field-name sets
  // below decide what gets sent, retried, or quarantined.
  const applyEditsToCollection = (changes) => {
    const current = getCachedAdmission(idRef.current);
    if (!current) return;
    const sparse = { _id: current._id };
    for (const { fieldPath, value } of changes) {
      const [root, itemId, ...rest] = fieldPath.split(".");
      if (!(root in sparse)) sparse[root] = structuredClone(current[root]);
      const isItemField = ["prescriptions", "questionRecords"].includes(root) && itemId !== undefined;
      const itemIndex = isItemField ? sparse[root].findIndex((item) => item._id === itemId) : -1;
      if (isItemField && itemIndex === -1) continue;
      for (const invalidField of [...invalidFieldsRef.current]) {
        if (invalidField === fieldPath || invalidField.startsWith(`${fieldPath}.`) || fieldPath.startsWith(`${invalidField}.`)) invalidFieldsRef.current.delete(invalidField);
      }
      dirtyFieldsRef.current.add(fieldPath);
      setValueAtPath(sparse, isItemField ? [root, itemIndex, ...rest].join(".") : fieldPath, value);
    }
    writeAdmission(sparse);
  };

  const scheduleFlush = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => flushAdmissionChanges(), 250);
  };

  const flushAdmissionChanges = async (extraFields) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (extraFields) applyEditsToCollection(Object.entries(extraFields).map(([field, value]) => ({ fieldPath: field, value })));

    const admissionId = idRef.current;
    const doc = getCachedAdmission(admissionId);
    const fields = new Set([...dirtyFieldsRef.current].filter((field) => !invalidFieldsRef.current.has(field)));
    if (!doc || fields.size === 0) {
      setHasPendingChanges(false);
      return;
    }

    dirtyFieldsRef.current = new Set([...dirtyFieldsRef.current].filter((field) => !fields.has(field)));
    inFlightFieldsRef.current = fields;
    setIsBackgroundLoading(true);
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(buildDeltaPayload(doc, fields)));
      const { ok, data, message, errors, effects } = await API.putFormData(`/admin/admission/${admissionId}`, formData);

      if (errors) {
        const errorPaths = Object.keys(errors);
        const isInvalid = (field) => errorPaths.some((errorPath) => field === errorPath || field.startsWith(`${errorPath}.`) || errorPath.startsWith(`${field}.`));
        let hasNewInvalidFields = false;
        for (const field of fields) {
          if (dirtyFieldsRef.current.has(field)) continue;
          if (isInvalid(field)) {
            invalidFieldsRef.current.add(field);
            hasNewInvalidFields = true;
          } else {
            dirtyFieldsRef.current.add(field);
          }
        }
        setValidationErrors([...new Set([...invalidFieldsRef.current, ...errorPaths])]);
        if (hasNewInvalidFields && [...dirtyFieldsRef.current].some((field) => !invalidFieldsRef.current.has(field))) scheduleFlush();
        return;
      }
      if (!ok) throw new Error(message);

      if (effects?.length > 0) {
        const modifiedPrescriptionIds = effects.filter((e) => e.type === "PRESCRIPTION_MODIFIED").map((e) => e.prescriptionId);
        if (modifiedPrescriptionIds.length > 0) setActionsAppliedToPrescriptions(modifiedPrescriptionIds);
        const incompatibleEffects = effects.filter((e) => e.type === "INCOMPATIBLE_CODES_REMOVED");
        if (incompatibleEffects.length > 0) setIncompatibleCodesRefused(incompatibleEffects);

        for (const e of effects.filter((e) => e.type === "DUPLICATE_ADMISSION")) {
          toast.custom((t) => (
            <div
              onClick={() => {
                toast.dismiss(t.id);
                history.push(`/admissions/${e.admissionId}`);
              }}
              className="cursor-pointer rounded-lg border bg-white px-4 py-3 text-sm shadow-lg"
            >
              Dossier dupliqué : <span className="font-medium text-blue-600">{e.number}</span>
            </div>
          ));
        }
      }

      setValidationErrors([...invalidFieldsRef.current]);
      // The PUT loads the admission without populating emails, so the response would
      // replace the populated array with ObjectIds; dropping the key keeps the local one.
      const freshAdmission = { ...data };
      delete freshAdmission.emails;
      upsertAdmission(freshAdmission);
      queryClient.invalidateQueries({ queryKey: ["audit-events"] });
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue lors de la sauvegarde des réponses");
      if (error?.code === "ADMISSION_STATUS_TRANSITION_DENIED") {
        fields.delete("status");
        getAdmissionDetails(admissionId);
      }
      for (const field of fields) if (!invalidFieldsRef.current.has(field)) dirtyFieldsRef.current.add(field);
    } finally {
      inFlightFieldsRef.current = new Set();
      setIsBackgroundLoading(false);
      setHasPendingChanges(dirtyFieldsRef.current.size > 0);
    }
  };

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (validationErrors.length > 0 || hasPendingChanges || isBackgroundLoading) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [validationErrors, hasPendingChanges, isBackgroundLoading]);

  const getAdmissionDetails = async (admissionId) => {
    if (!admissionId) return;
    if (admissionId !== id) {
      history.replace({ pathname: `/admissions/${admissionId}`, search: window.location.search, state: history.location.state });
    }
    await getAdmission(admissionId).catch(() => {});
  };

  const onAdmissionChange = (data) => {
    if (!admission || isLocked) return;
    applyEditsToCollection(data.map(({ field, value }) => ({ fieldPath: field.replace(/\[(\d+)\]/g, ".$1"), value })));
    setHasPendingChanges(true);
    scheduleFlush();
  };

  const computeBlockingItems = () => {
    if (!admission) return [];

    const items = [];
    const patient = admission.patient || {};
    const prescriptions = admission.prescriptions || [];

    const isValueEmpty = (value) => {
      if (value === undefined || value === null) return true;
      if (typeof value === "string") return value.trim() === "";
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    const checkField = (fieldState, value, label, focusField) => {
      if (isRequiredFieldState(fieldState) && isValueEmpty(value)) {
        items.push({ label, focusField, productionOnly: false, forceable: fieldState === FIELD_STATE.REQUIRE_FORCEABLE });
      }
    };

    checkField(activeFields?.admission?.establishment, admission?.establishment, "Établissement", "establishment");
    checkField(activeFields?.admission?.silReservationNumber, admission?.silReservationNumber, "N° de réservation", "silReservationNumber");
    checkField(activeFields?.admission?.silAdmissionNumber, admission?.silAdmissionNumber, "N° dossier SIL", "silAdmissionNumber");
    checkField(activeFields?.admission?.samplingDate, admission?.samplingDate, "Date et heure de prélèvement", "samplingDate");
    checkField(activeFields?.admission?.samplerCode, admission?.samplerCode || admission?.laboratory?.samplerCode, "Préleveur", "samplerCode");

    checkField(activeFields?.patient?.firstName, patient?.firstName, "Prénom", "patient.firstName");
    checkField(activeFields?.patient?.lastName, patient?.lastName, "Nom", "patient.lastName");
    checkField(activeFields?.patient?.birthLastName, patient?.birthLastName, "Nom de naissance", "patient.birthLastName");
    checkField(activeFields?.patient?.sexe, patient?.sexe, "Sexe", "patient.sexe");
    checkField(activeFields?.patient?.birthdate, patient?.birthdate, "Date de naissance", "patient.birthdate");
    checkField(activeFields?.patient?.birthplaceCode, patient?.birthplaceCode, "Lieu de naissance", "patient.birthplaceCode");
    checkField(activeFields?.patient?.INS, patient?.INS?.value, "Numéro de sécurité sociale", "patient.INS.value");
    checkField(activeFields?.patient?.IPP, patient?.IPP, "IPP", "patient.IPP");
    checkField(activeFields?.patient?.admissionId, patient?.admissionId, "Numéro d'admission", "patient.admissionId");
    checkField(activeFields?.patient?.hostService, patient?.hostService, "Service", "patient.hostService");
    checkField(activeFields?.patient?.room, patient?.room, "Chambre", "patient.room");
    checkField(activeFields?.patient?.phone, patient?.phone, "Téléphone", "patient.phone");
    checkField(activeFields?.patient?.email, patient?.email, "Email", "patient.email");
    checkField(activeFields?.patient?.address, patient?.address, "Adresse", "patient.address");
    if (activeFields?.patient?.address !== FIELD_STATE.DISABLE) {
      checkField(activeFields?.patient?.zipCode, patient?.zipCode, "Code postal", "patient.address");
      checkField(activeFields?.patient?.city, patient?.city, "Ville", "patient.address");
    }
    checkField(activeFields?.patient?.weight, patient?.weight, "Poids", "patient.weight");
    checkField(activeFields?.patient?.height, patient?.height, "Taille", "patient.height");

    const mutuelle = patient?.mutuelle || {};
    checkField(activeFields?.mutuelle?.code, mutuelle?.code, "Code mutuelle", "patient.mutuelle.code");
    checkField(activeFields?.mutuelle?.adherentNumber, mutuelle?.adherentNumber, "Numéro d'adhérent", "patient.mutuelle.adherentNumber");
    checkField(activeFields?.mutuelle?.rightsFrom, mutuelle?.rightsFrom, "Droits du", "patient.mutuelle.rightsFrom");
    checkField(activeFields?.mutuelle?.rightsTo, mutuelle?.rightsTo, "Droits au", "patient.mutuelle.rightsTo");

    const prescriptionStates = ["silPrescriptionNumber", "date", "doctor"].map((key) => activeFields?.prescription?.[key]);
    const prescriptionsState = prescriptionStates.includes(FIELD_STATE.REQUIRE)
      ? FIELD_STATE.REQUIRE
      : prescriptionStates.includes(FIELD_STATE.REQUIRE_FORCEABLE)
        ? FIELD_STATE.REQUIRE_FORCEABLE
        : FIELD_STATE.OPTIONAL;
    checkField(prescriptionsState, prescriptions, "Aucune ordonnance", "prescriptions");
    prescriptions.forEach((prescription, index) => {
      const suffix = prescriptions.length > 1 ? ` (ordonnance ${index + 1})` : "";
      checkField(activeFields?.prescription?.silPrescriptionNumber, prescription?.hprim?.silPrescriptionNumber, `ID externe${suffix}`, `prescriptions.${index}.hprim.silPrescriptionNumber`);
      checkField(activeFields?.prescription?.date, prescription?.date, `Date de prescription${suffix}`, `prescriptions.${index}.date`);
      checkField(
        activeFields?.prescription?.doctor,
        autocompleteSettings.doctor ? prescription?.doctor?.code : prescription?.doctor?.RPPS,
        `Médecin${suffix}`,
        `prescriptions.${index}.doctor`,
      );
    });

    for (const questionRecord of admission.questionRecords) {
      if (isValueEmpty(questionRecord.answer) && !questionRecord.autoAnswer && questionRecord.isRequired !== false) {
        items.push({
          label: questionRecord.question || "Question sans intitulé",
          focusField: `questionRecords.${questionRecord._id}`,
          productionOnly: Boolean(questionRecord.patient || questionRecord.ide),
          forceable: false,
        });
      }
    }

    return items;
  };

  const sendAdmissionToProduction = async () => {
    if (!admission) return;

    await flushAdmissionChanges();

    setIsBackgroundLoading(true);
    try {
      const { ok, data } = await API.post(`/admin/admission/${admission._id}/production`);
      if (!ok) throw data;
      toast.success("Dossier envoyé en production avec succès");
      await nextAdmission();
    } catch (error) {
      if (error?.code === "UNANSWERED_QUESTIONS") {
        toast.error("De nouvelles questions ont été ajoutées à l'admission. Merci de les vérifier avant de valider.");
        await getAdmissionDetails(admission._id);
      } else if (error?.code === "INCOMPLETE_ADMISSION") {
        toast.error(error.message || "Le dossier est incomplet et ne peut pas être envoyé en production");
        await getAdmissionDetails(admission._id);
      } else {
        capture(error);
        toast.error("Une erreur est survenue lors de l'envoi en production");
      }
    }
    setIsBackgroundLoading(false);
  };

  const nextAdmission = async () => {
    history.replace(`/admissions${window.location.search}`);
  };

  const validateAdmission = async () => {
    await flushAdmissionChanges({ status: "VALIDATED" });

    if (admission?.source === "IDE") return await nextAdmission();

    const hasUnansweredQuestions = admission?.questionRecords?.some(
      (qr) => qr.canAnswerInPreRegistration && qr.patient && qr.patientIsRequired !== false && !qr.answer && !qr.autoAnswer,
    );
    const hasNewGuidelines = admission?.guidelines?.some((g) => !originalGuidelineIds.includes(g.guidelineId));
    const hasPatientEmail = admission?.patient?.email;

    const hasConfirmationEmail = admission?.emails?.some((e) => e.type === EMAIL_TYPE.CONFIRMATION);
    if (!hasConfirmationEmail && hasPatientEmail && admission?.type === "LATER") {
      setEmailPreviewType(EMAIL_TYPE.CONFIRMATION);
      return setShowEmailPreviewModal(true);
    }

    if ((hasUnansweredQuestions || hasNewGuidelines) && hasPatientEmail) {
      setEmailPreviewType(EMAIL_TYPE.MORE_QUESTIONS);
      return setShowEmailPreviewModal(true);
    }

    await nextAdmission();
  };

  const toggleHold = async () => {
    const nextStatus = admission?.status === "ON_HOLD" ? "TO_VALIDATE" : "ON_HOLD";
    await flushAdmissionChanges({ status: nextStatus });
  };

  const forceAdmissionStatus = async ({ status, statusReason }) => {
    await flushAdmissionChanges();

    setIsBackgroundLoading(true);
    try {
      const formData = new FormData();
      const payload = { _id: admission._id, status };
      if (statusReason !== undefined) payload.statusReason = statusReason;
      formData.append("data", JSON.stringify(payload));
      const { ok, data, message } = await API.putFormData(`/admin/admission/${admission._id}`, formData);
      if (!ok) throw new Error(message);

      const freshAdmission = { ...data };
      delete freshAdmission.emails;
      upsertAdmission(freshAdmission);
      queryClient.invalidateQueries({ queryKey: ["audit-events"] });
      setForceStatusTarget(null);
      toast.success("Statut mis à jour");
    } catch (error) {
      capture(error);
      toast.error(error.message || "Impossible de forcer le statut");
    }
    setIsBackgroundLoading(false);
  };

  const onGuidelineValidatedChange = async (guidelineRecordId, validated) => {
    const previousGuidelines = admission.guidelines;
    writeAdmission({ _id: admission._id, guidelines: previousGuidelines.map((g) => (g._id === guidelineRecordId ? { ...g, validated } : g)) });

    try {
      const { ok, data, message } = await API.put(`/admin/admission/${admission._id}/guideline/${guidelineRecordId}`, { validated });
      if (!ok) throw new Error(message);
      writeAdmission({ _id: admission._id, guidelines: data });
    } catch (error) {
      writeAdmission({ _id: admission._id, guidelines: previousGuidelines });
      capture(error);
      toast.error(error.message || "Impossible de mettre à jour la recommandation");
    }
  };

  const blockingItems = admission ? computeBlockingItems() : [];
  const validationBlockers = blockingItems.filter((item) => item.productionOnly !== true);
  const secretaryGuidelines = (admission?.guidelines || []).filter((g) => g.secretary);
  const unvalidatedGuidelines = secretaryGuidelines.filter((g) => g.validated !== true);
  const isUrgent = (!!admission?.checkInDate && ![ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED].includes(admission?.status)) || admission?.source === "IDE";

  const missingLabelsSummary = (blockers) => {
    const labels = blockers.map((blocker) => blocker.label);
    if (labels.length <= 3) return labels.join(", ");
    return `${labels.slice(0, 3).join(", ")} et ${labels.length - 3} autre${labels.length - 3 > 1 ? "s" : ""}`;
  };

  const onValidateAttempt = () => {
    const hardBlockers = validationBlockers.filter((item) => !item.forceable);
    if (hardBlockers.length > 0) {
      toast.error(`Le dossier n'a pas été validé, champs manquants : ${missingLabelsSummary(hardBlockers)}`);
      setActiveTab("overview");
      setTimeout(() => onFocusBlockingItem(hardBlockers[0].focusField), 0);
      return;
    }
    const forceableBlockers = validationBlockers.filter((item) => item.forceable);
    if (forceableBlockers.length > 0 || unvalidatedGuidelines.length > 0) {
      return setSubmitConfirmation({ action: "VALIDATE", missingFields: forceableBlockers, withGuidelines: unvalidatedGuidelines.length > 0 });
    }
    validateAdmission();
  };

  const onSendToProductionAttempt = () => {
    const hardBlockers = blockingItems.filter((item) => !item.forceable);
    if (hardBlockers.length > 0) {
      toast.error(`Le dossier n'a pas été envoyé en production, champs manquants : ${missingLabelsSummary(hardBlockers)}`);
      setHasBlockedProduction(true);
      setActiveTab("overview");
      setTimeout(() => onFocusBlockingItem(hardBlockers[0].focusField), 0);
      return;
    }
    const forceableBlockers = blockingItems.filter((item) => item.forceable);
    if (forceableBlockers.length > 0 || unvalidatedGuidelines.length > 0) {
      return setSubmitConfirmation({ action: "PRODUCTION", missingFields: forceableBlockers, withGuidelines: unvalidatedGuidelines.length > 0 });
    }
    sendAdmissionToProduction();
  };

  const onConfirmSubmit = async () => {
    const { action } = submitConfirmation;
    setSubmitConfirmation(null);
    if (action === "PRODUCTION") return await sendAdmissionToProduction();
    await validateAdmission();
  };

  const canSubmit = Boolean(admission) && !isLocked && !isBackgroundLoading && validationErrors.length === 0;
  useHotkeys(SHORTCUTS.SAVE.keys, () => onValidateAttempt(), { preventDefault: true, enabled: canSubmit }, [canSubmit, validationBlockers, blockingItems]);
  useHotkeys(SHORTCUTS.SAVE_AND_SEND.keys, () => onSendToProductionAttempt(), { preventDefault: true, enabled: canSubmit }, [canSubmit, validationBlockers, blockingItems]);
  useHotkeys(SHORTCUTS.REJECT.keys, () => setShowRejectModal(true), { preventDefault: true, enabled: !!admission && !isLocked }, [admission, isLocked]);
  useHotkeys(
    SHORTCUTS.BACK.keys,
    () => {
      let closedLocalUi = false;
      if (admissionHeaderRef.current?.closeUi?.() === true) closedLocalUi = true;
      if (admissionOverviewRef.current?.closeUi?.() === true) closedLocalUi = true;

      const hasSceneModal = showEmailPreviewModal || showRejectModal || forceStatusTarget !== null || submitConfirmation !== null;
      if (hasSceneModal || closedLocalUi) {
        setShowEmailPreviewModal(false);
        setShowRejectModal(false);
        setForceStatusTarget(null);
        setSubmitConfirmation(null);
        return;
      }
      flushAdmissionChanges();
      history.push("/admissions");
    },
    { preventDefault: true },
    [showEmailPreviewModal, showRejectModal, forceStatusTarget, submitConfirmation, history],
  );

  const filteredIds = history.location.state?.filteredIds || [];

  const navigateToOffset = (offset) => {
    if (!filteredIds.length) return;
    const currentIndex = filteredIds.indexOf(id);
    if (currentIndex < 0) return;
    const nextIndex = (currentIndex + offset + filteredIds.length) % filteredIds.length;
    const nextId = filteredIds[nextIndex];
    if (nextId === id) return;

    // Warm one hop past the destination in the direction of travel so a sustained
    // j/k streak keeps hitting the admissions cache instead of the (slow) backend.
    const lookaheadIndex = (nextIndex + offset + filteredIds.length) % filteredIds.length;
    const lookaheadId = filteredIds[lookaheadIndex];
    if (lookaheadId !== nextId) prefetchAdmission(lookaheadId);

    flushAdmissionChanges();

    history.replace({ pathname: `/admissions/${nextId}`, state: { filteredIds } });
  };

  // On arrival at a new admission (from the list, a URL, or the first j/k),
  // prefetch both immediate neighbors while the browser is idle so the first
  // key press after landing is as cheap as subsequent ones.
  useEffect(() => {
    if (!id || !filteredIds.length) return;
    const currentIndex = filteredIds.indexOf(id);
    if (currentIndex < 0) return;

    const schedule = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = schedule(() => {
      const nextId = filteredIds[(currentIndex + 1) % filteredIds.length];
      const prevId = filteredIds[(currentIndex - 1 + filteredIds.length) % filteredIds.length];
      prefetchAdmission(nextId);
      if (prevId !== nextId) prefetchAdmission(prevId);
    });
    return () => cancel(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filteredIds.length]);

  useHotkeys(SHORTCUTS.NAV_NEXT.keys, () => navigateToOffset(1), { preventDefault: true }, [filteredIds, id]);
  useHotkeys(SHORTCUTS.NAV_PREV.keys, () => navigateToOffset(-1), { preventDefault: true }, [filteredIds, id]);

  useHotkeys(
    SHORTCUTS.TAB_PREV.keys,
    () =>
      setActiveTab((current) => {
        const idx = TABS.findIndex((t) => t.id === current);
        return TABS[(idx - 1 + TABS.length) % TABS.length].id;
      }),
    { preventDefault: true },
  );
  useHotkeys(
    SHORTCUTS.TAB_NEXT.keys,
    () =>
      setActiveTab((current) => {
        const idx = TABS.findIndex((t) => t.id === current);
        return TABS[(idx + 1) % TABS.length].id;
      }),
    { preventDefault: true },
  );

  const getSettings = async () => {
    try {
      const { ok, data } = await API.get("/setting");
      if (!ok) throw new Error("Failed to get visible fields");
      setDefaultActiveFields(data.activeFields);
      setAutocompleteSettings(data.autocomplete);
    } catch (error) {
      capture(error);
    }
  };

  const getLaboratories = async () => {
    try {
      const { ok, data } = await API.get("/admin/laboratory");
      if (!ok) throw new Error("Failed to get laboratories");
      setLaboratories(data);
    } catch (error) {
      capture(error);
      toast.error("Impossible de récupérer la liste des laboratoires");
    }
  };

  const onScrollToImage = useCallback((documentId) => {
    admissionDocumentsViewerRef.current?.scrollToImage?.(documentId);
  }, []);

  const onFocusBlockingItem = (focusField) => {
    if (!focusField) return;
    const target = detailsScrollRef.current?.querySelector(`[data-focus-field="${focusField}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.querySelector("input:not([disabled]), select:not([disabled]), textarea, button")?.focus({ preventScroll: true });
  };

  const sharedNumberField = admission?.silReservationNumber ? "silReservationNumber" : "silAdmissionNumber";
  const sharedNumber = admission?.[sharedNumberField];
  const { data: sharedNumberAdmissions = [] } = useQuery({
    queryKey: ["admissions", { [sharedNumberField]: sharedNumber, laboratoryId: admission?.laboratory?._id }],
    queryFn: async () => {
      const response = await API.get(`/admin/admission?${buildServerParams({ [sharedNumberField]: sharedNumber, laboratoryId: admission.laboratory?._id })}`);
      if (!response.ok) throw new Error(response.message);
      return response.data.admissions;
    },
    enabled: Boolean(sharedNumber),
  });
  const duplicationRootId = admission?.parentAdmissionId || admission?._id;
  const sameNumberAdmissions = sharedNumberAdmissions.filter(
    (sharedNumberAdmission) =>
      sharedNumberAdmission._id !== admission?._id &&
      sharedNumberAdmission.status !== ADMISSION_STATUS.ARCHIVED &&
      sharedNumberAdmission._id !== duplicationRootId &&
      sharedNumberAdmission.parentAdmissionId !== duplicationRootId,
  );

  const displayAdmission = admission?._id === id ? admission : preview;
  const historyIndicator =
    lastProcessingEvent?.status === "SUCCESS" ? "bg-emerald-500" : lastProcessingEvent?.status === "ERROR" ? "bg-red-500" : lastProcessingEvent ? "bg-amber-500" : null;

  return (
    <>
      <Prompt
        when={validationErrors.length > 0 || hasPendingChanges}
        message={
          validationErrors.length > 0
            ? "Le dossier actuel contient des erreurs de validation non résolues. Voulez-vous vraiment quitter cette page ?"
            : "Le dossier actuel est en cours de sauvegarde. Voulez-vous vraiment quitter cette page ?"
        }
      />
      <LinearProgressBar isVisible={isBackgroundLoading} />

      <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
        {admission || preview || isFetchingAdmission ? (
          <PrescriptionHoverProvider>
            <AdmissionHeader
              ref={admissionHeaderRef}
              currentAdmissionId={id}
              filteredIds={filteredIds}
              displayAdmission={displayAdmission}
              admission={admission}
              isBackgroundLoading={isBackgroundLoading}
              hasValidationErrors={validationErrors.length > 0}
              nbConcurrentUsers={presenceCounts[admission?._id] || 0}
              onClose={() => {
                flushAdmissionChanges();
                history.push("/admissions");
              }}
              onNavigateToOffset={(offset) => {
                navigateToOffset(offset);
                notifyShortcut(offset > 0 ? "NAV_NEXT" : "NAV_PREV");
              }}
              onValidate={() => {
                onValidateAttempt();
                notifyShortcut("SAVE");
              }}
              onSendToProduction={() => {
                onSendToProductionAttempt();
                notifyShortcut("SAVE_AND_SEND");
              }}
              onReject={() => {
                setShowRejectModal(true);
                notifyShortcut("REJECT");
              }}
              onToggleHold={toggleHold}
              onForceStatus={setForceStatusTarget}
              onOpenAuditLog={() => {
                setActiveTab("history");
                queryClient.invalidateQueries({ queryKey: ["audit-events"] });
              }}
            />
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {admission?._id !== id ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
                  <span className="text-sm text-gray-400">Chargement...</span>
                </div>
              ) : (
                <>
                  <div className={`relative flex min-w-0 flex-col ${viewerLayoutMode === "attach" ? "flex-[4]" : "flex-1"}`}>
                    <TabBar activeTab={activeTab} onTabChange={setActiveTab} counts={{ emails: admission?.emails?.length || 0 }} indicators={{ history: historyIndicator }} />
                    {isLocked && (
                      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2 text-[12px]">
                        <LuLock className="h-3.5 w-3.5 shrink-0 text-primary-500" />
                        <span className="font-medium uppercase tracking-wider text-gray-600">Lecture seule</span>
                        <span className="text-gray-300">—</span>
                        <span className="truncate text-gray-700">Dossier {STATUS_CONFIG[admission.status]?.label?.toLowerCase()} : dupliquez le dossier pour le corriger.</span>
                      </div>
                    )}
                    {sameNumberAdmissions.length > 0 && (
                      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2 text-[12px]">
                        <LuTriangleAlert className="h-3.5 w-3.5 shrink-0 text-orange-500" />
                        <span className="shrink-0 font-medium uppercase tracking-wider text-orange-600">{sharedNumberField === "silReservationNumber" ? "N° de réservation en double" : "N° dossier SIL en double"}</span>
                        <span className="text-gray-300">—</span>
                        <span className="truncate text-gray-700">
                          {sharedNumber} est aussi utilisé par{" "}
                          {sameNumberAdmissions.map((sameNumberAdmission, index) => {
                            const patientName = [sameNumberAdmission.patient?.firstName, sameNumberAdmission.patient?.lastName].filter(Boolean).join(" ");
                            const statusLabel = STATUS_CONFIG[sameNumberAdmission.status].label.toLowerCase();
                            return (
                              <span key={sameNumberAdmission._id}>
                                {index > 0 && ", "}
                                <button
                                  type="button"
                                  onClick={() => {
                                    flushAdmissionChanges();
                                    history.push(`/admissions/${sameNumberAdmission._id}`);
                                  }}
                                  className="font-mono text-[11px] uppercase tracking-wide text-primary-600 hover:underline"
                                >
                                  {sameNumberAdmission.number}
                                </button>
                                <span className="text-gray-500"> ({[patientName, statusLabel].filter(Boolean).join(", ")})</span>
                              </span>
                            );
                          })}
                        </span>
                      </div>
                    )}
                    <div ref={detailsScrollRef} className="relative min-h-0 flex-1 overflow-auto bg-gray-50 pb-12" key={admission?._id}>
                      {validationErrors.length > 0 && (
                        <div className="pointer-events-none sticky top-0 z-10 h-0">
                          <div className="pointer-events-auto absolute right-2 top-0 flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-300 px-2 py-1">
                            <LuTriangleAlert className="h-4 w-4" />
                          </div>
                        </div>
                      )}
                      <fieldset disabled={isLocked} className="min-w-0">
                        <AdmissionOverview
                          ref={admissionOverviewRef}
                          isActive={activeTab === "overview"}
                          admission={admission}
                          activeFields={activeFields}
                          validationErrors={validationErrors}
                          showProductionRequired={hasBlockedProduction || isUrgent}
                          laboratories={laboratories}
                          autocompleteSettings={autocompleteSettings}
                          actionsAppliedToPrescriptions={actionsAppliedToPrescriptions}
                          incompatibleCodesRefused={incompatibleCodesRefused}
                          isBackgroundLoading={isBackgroundLoading}
                          onAdmissionChange={onAdmissionChange}
                          onScrollToImage={onScrollToImage}
                          ProcessingStatusIcon={ProcessingStatusIcon}
                          onAdmissionUpdated={upsertAdmission}
                          onGetAdmissionDetails={getAdmissionDetails}
                          onSetBackgroundLoading={setIsBackgroundLoading}
                          onGuidelineValidatedChange={onGuidelineValidatedChange}
                        />
                      </fieldset>
                      {activeTab === "emails" && (
                        <CommunicationsSection
                          emails={admission?.emails || []}
                          onSendEmail={() => setShowEmailPreviewModal(true)}
                          disableEmailSending={!admission?.patient?.email}
                        />
                      )}
                      {activeTab === "history" && (
                        <div className="mx-auto max-w-3xl px-6 py-4">
                          <AuditJournal collectionName="admissions" documentId={admission?._id} user={user} />
                        </div>
                      )}
                    </div>
                  </div>

                  <AdmissionDocumentsViewer
                    ref={admissionDocumentsViewerRef}
                    admission={admission}
                    isFetchingAdmission={isFetchingAdmission}
                    isLocked={isLocked}
                    onAdmissionUpdated={upsertAdmission}
                    onLayoutModeChange={setViewerLayoutMode}
                    ProcessingStatusIcon={ProcessingStatusIcon}
                  />
                </>
              )}
            </div>
          </PrescriptionHoverProvider>
        ) : (
          <div className="flex flex-1 items-center justify-center text-gray-400">Admission introuvable</div>
        )}
      </div>

      {showRejectModal && (
        <RejectModal
          setShowRejectModal={setShowRejectModal}
          updateAdmission={flushAdmissionChanges}
          nextAdmission={nextAdmission}
          setEmailPreviewType={setEmailPreviewType}
          setShowEmailPreviewModal={setShowEmailPreviewModal}
          loading={isBackgroundLoading}
          admissionSelected={admission}
        />
      )}

      {forceStatusTarget && (
        <ForceStatusModal
          fromStatus={admission?.status}
          toStatus={forceStatusTarget}
          loading={isBackgroundLoading}
          onClose={() => setForceStatusTarget(null)}
          onConfirm={forceAdmissionStatus}
        />
      )}

      {submitConfirmation && (
        <SubmitConfirmModal
          missingFields={submitConfirmation.missingFields}
          guidelines={submitConfirmation.withGuidelines ? secretaryGuidelines : []}
          action={submitConfirmation.action}
          loading={isBackgroundLoading}
          onGuidelineValidatedChange={onGuidelineValidatedChange}
          onFocusField={(focusField) => {
            setActiveTab("overview");
            setTimeout(() => onFocusBlockingItem(focusField), 0);
          }}
          onClose={() => setSubmitConfirmation(null)}
          onConfirm={onConfirmSubmit}
        />
      )}

      {showEmailPreviewModal && (
        <EmailPreviewModal
          admissionSelected={admission}
          onClose={() => {
            setShowEmailPreviewModal(false);
            getAdmissionDetails(admission._id);
          }}
          loading={isBackgroundLoading}
          setLoading={setIsBackgroundLoading}
          type={emailPreviewType}
        />
      )}
    </>
  );
};

export default AdmissionDetail;
